import * as Sentry from "@sentry/browser";
import { Col, Divider, Row, Table, message } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import { AxiosResponse } from "axios";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  APIError,
  FormFieldConfig,
  PreOpportunityInteraction,
  SelectOptionsInterface,
  User,
} from "../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";

interface State {
  showExtraFields: boolean;
  isLoading: boolean;
  interactionSources: SelectOptionsInterface[];
  opportunityProspects: SelectOptionsInterface[];
  preOpportunityInteractions: PreOpportunityInteraction[];
}

interface RouteParams {
  id?: string;
}

export interface Props extends RouteComponentProps<RouteParams> {
  user: User;
}

const OPPORTUNITY_DATA = `
  query   {
    interactionSources {
      id
      name
    }
    opportunityProspects {
      id
      name
    }
  }
`;

const CREATE_INTERACTION_MUTATION = `
mutation CreatePreOpportunityInteraction($activityDate: String!, $userId: ID!, $description: String!, $interactionSourceId: ID!, $opportunityProspectId: ID!, $phoneNumber: String!, $emailAddress: String, $firstName: String, $lastName: String ) {
  createPreOpportunityInteraction(activityDate: $activityDate, userId: $userId, description: $description, interactionSourceId: $interactionSourceId, opportunityProspectId: $opportunityProspectId,  phoneNumber: $phoneNumber, emailAddress: $emailAddress, firstName: $firstName, lastName: $lastName) {
    preOpportunityInteraction {
      id
    }
  }
}
`;

const LOAD_INTERACTIONS_BY_PHONE = `
query LookupPreOpportunityInteractions($phoneNumber: String!) {
  lookupPreOpportunityInteractions(phoneNumber: $phoneNumber) {
    id
    description
    firstName
    lastName
    activityDate
    email {
      id
      address
    }
    interactionSource {
      id
      name
    }
    opportunityProspect {
      id
      name
    }
    phone {
      id
      number
    }
  }
}
`;

export const columns = [
  {
    title: "Activity Date",
    dataIndex: "activityDate",
    key: "activityDate",
    render(text: string, record: PreOpportunityInteraction): JSX.Element {
      return <span>{record.activityDate}</span>;
    },
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    render(text: string, record: PreOpportunityInteraction): JSX.Element {
      return <span>{record.email?.address}</span>;
    },
  },
  {
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    render(text: string, record: PreOpportunityInteraction): JSX.Element {
      return <span>{record.phone.number}</span>;
    },
  },
  {
    title: "First Name",
    dataIndex: "firstName",
    key: "firstName",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Last Name",
    dataIndex: "lastName",
    key: "lastName",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Source",
    dataIndex: "source",
    key: "source",
    render(text: string, record: PreOpportunityInteraction): JSX.Element {
      return <span>{record.interactionSource.name}</span>;
    },
  },
  {
    title: "Prospect",
    dataIndex: "prospect",
    key: "prospect",
    render(text: string, record: PreOpportunityInteraction): JSX.Element {
      return <span>{record.opportunityProspect.name}</span>;
    },
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    render(text: string, record: PreOpportunityInteraction): JSX.Element {
      return <span>{record.description}</span>;
    },
  },
];

export default class PreOpportunityInteractions extends React.Component<
  Props,
  State
> {
  searchByPhoneReference: RefObject<FormInstance>;
  createReference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.searchByPhoneReference = React.createRef<FormInstance>();
    this.createReference = React.createRef<FormInstance>();
    this.state = {
      showExtraFields: false,
      isLoading: false,
      interactionSources: [],
      opportunityProspects: [],
      preOpportunityInteractions: [],
    };
  }

  setRef = (
    name: "searchByPhoneReference" | "createReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  loadInteractionsByPhoneQuery = async (phoneNumber: string): Promise<void> => {
    interface Response {
      data: {
        lookupPreOpportunityInteractions: PreOpportunityInteraction[];
      };
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: LOAD_INTERACTIONS_BY_PHONE,
        variables: {
          phoneNumber,
        },
      });

      this.setState({
        preOpportunityInteractions:
          response.data.data.lookupPreOpportunityInteractions,
      });
    } catch (error) {
      message.error(`failed to load interactions - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({
      isLoading: false,
      showExtraFields: true,
    });
  };

  loadInteractionsByPhone = async (): Promise<void> => {
    const fieldsToBeValidated = ["phoneNumber"];
    if (!this.searchByPhoneReference.current) return;
    let data;

    try {
      data = await this.searchByPhoneReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.loadInteractionsByPhoneQuery(data.phoneNumber);
  };

  createInteraction = async (): Promise<void> => {
    interface Response {
      createPreOpportunityInteraction: PreOpportunityInteraction;
      errors: APIError[];
    }

    const fieldsToBeValidated = [
      "phoneNumber",
      "activityDate",
      "emailAddress",
      "firstName",
      "lastName",
      "interactionSourceId",
      "opportunityProspectId",
      "description",
    ];

    if (!this.createReference.current) return;
    if (!this.searchByPhoneReference.current) return;

    let data;

    try {
      data = await this.createReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_INTERACTION_MUTATION,
        variables: {
          ...data,
          userId: this.props.user.id,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "createPreOpportunityInteraction",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.createReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.loadInteractionsByPhoneQuery(data.phoneNumber);
        this.searchByPhoneReference?.current.setFields([
          {
            name: "phoneNumber",
            value: data.phoneNumber,
          },
        ]);

        this.createReference.current.resetFields();
      }
    } catch (error) {
      message.error(`failed to create interaction - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({
      isLoading: false,
    });
  };

  getOpportunityCreationData(): void {
    request({
      query: OPPORTUNITY_DATA,
    }).then(({ data }: AxiosResponse<any>): void => {
      this.setState({
        interactionSources: data.data.interactionSources,
        opportunityProspects: data.data.opportunityProspects,
      });
    });
  }

  componentDidMount(): void {
    this.getOpportunityCreationData();
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      interactionSources,
      opportunityProspects,
      preOpportunityInteractions,
      showExtraFields,
    } = this.state;

    const searchByPhoneFields: FormFieldConfig[] = [
      {
        id: "phoneNumber",
        name: "phoneNumber",
        inputType: "phone",
        rule: "string",
        label: "Phone",
        placeholder: "Phone",
        required: true,
        style: { marginRight: "40px" },
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 5,
        },
      },
      {
        id: "searchButton",
        name: "searchButton",
        inputType: "button",
        value: "search",
        required: true,
        className: "base-button",
        elementStyle: {
          position: "relative",
          top: "32px",
        },
        handleClick: this.loadInteractionsByPhone,
        colConfig: {
          span: 2,
        },
      },
    ];

    const createInteractionFields: FormFieldConfig[] = [
      {
        id: "phoneNumber",
        name: "phoneNumber",
        inputType: "phone",
        rule: "string",
        label: "Phone",
        placeholder: "Phone",
        required: true,
        initialValue:
          this.searchByPhoneReference.current?.getFieldValue("phoneNumber"),
        style: { marginRight: "40px" },
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "activityDate",
        name: "activityDate",
        inputType: "defaultDate",
        rule: "object",
        required: true,
        label: "Activity Date",
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "emailAddress",
        name: "emailAddress",
        inputType: "string",
        rule: "email",
        label: "Email",
        placeholder: "Email Address",
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "firstName",
        name: "firstName",
        inputType: "string",
        rule: "string",
        placeholder: "First Name",
        label: "First Name",
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "lastName",
        name: "lastName",
        inputType: "string",
        rule: "string",
        placeholder: "Last Name",
        label: "Last Name",
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "interactionSourceId",
        name: "interactionSourceId",
        inputType: "select",
        rule: "string",
        label: "Interaction Source",
        placeholder: "Interaction Source",
        required: true,
        colConfig: {
          span: 24,
        },

        selectOptions: interactionSources,
      },
      {
        id: "opportunityProspectId",
        name: "opportunityProspectId",
        inputType: "select",
        rule: "string",
        placeholder: "Opportunity Prospect",
        label: "Opportunity Prospect",
        required: true,
        colConfig: {
          span: 24,
        },
        selectOptions: opportunityProspects,
      },
      {
        id: "description",
        name: "description",
        required: true,
        inputType: "textArea",
        rule: "string",
        label: "Description",
        formItemStyle: { marginBottom: "12px" },
        autoSize: { minRows: 6, maxRows: 6 },
        placeholder: "Description",
        colConfig: {
          span: 24,
        },
      },
      {
        id: "createInteraction",
        name: "createInteraction",
        inputType: "button",
        value: "Create",
        required: true,
        className: "base-button",
        handleClick: this.createInteraction,
        colConfig: {
          span: 4,
        },
      },
    ];

    return (
      <Card header="Interaction">
        <Fragment>
          <Row>
            <Col span={24}>
              <BaseForm
                fieldsToHide={[]}
                layout="vertical"
                reference={this.searchByPhoneReference}
                fields={searchByPhoneFields}
              ></BaseForm>
            </Col>
          </Row>
          {showExtraFields && (
            <Fragment>
              <Divider />
              <Row>
                <Col span={6}>
                  <BaseForm
                    layout="vertical"
                    fieldsToHide={[]}
                    reference={this.createReference}
                    fields={createInteractionFields}
                  ></BaseForm>
                </Col>
                <Col span={17} offset={1}>
                  <Table
                    columns={columns}
                    dataSource={preOpportunityInteractions}
                    loading={isLoading}
                    pagination={false}
                  />
                </Col>
              </Row>
            </Fragment>
          )}
        </Fragment>
      </Card>
    );
  }
}
